import { useCallback, useEffect, useRef, useState } from "react";
import {
  callPublicTool,
  fetchPublicHallAisles,
  fetchPublicHallBooths,
  ApiError,
} from "./api";
import { BoothSearchField } from "./components/BoothSearchField";
import { FloorWorkspace, type FloorDestination } from "./floor/FloorWorkspace";
import { resolveMapHall } from "./floor/mapHall";
import { formatBoothHit, type BoothSearchHit } from "./floor/boothLookup";
import { useCampaignFilters } from "./campaignFilters";
import { EVENT_KEY, DEFAULT_CAMPAIGN_KEY, IRONFLEET_BOOTH } from "./constants";
import type { AisleGraph, HallBoothCollection, LoadState } from "./types";

function toLoadError(error: unknown, fallback: string): LoadState<never> {
  return {
    status: "error",
    message: error instanceof Error ? error.message : fallback,
  };
}

export default function FloorWalkerRoute() {
  const [hallId, setHallId] = useState("A");
  const [hallGeo, setHallGeo] = useState<LoadState<HallBoothCollection>>({ status: "loading" });
  const [aisleState, setAisleState] = useState<LoadState<AisleGraph>>({ status: "idle" });
  const [selectedBooth, setSelectedBooth] = useState(IRONFLEET_BOOTH);
  const [checkInBooth, setCheckInBooth] = useState(IRONFLEET_BOOTH);
  const [checkInQuery, setCheckInQuery] = useState(IRONFLEET_BOOTH);
  const [destination, setDestination] = useState<FloorDestination | null>(null);
  const [goingToBooth, setGoingToBooth] = useState("");
  const [floorSearch, setFloorSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useCampaignFilters();
  const searchPickRef = useRef<((hit: BoothSearchHit) => void) | null>(null);

  const loadHallMap = useCallback(async (hall: string) => {
    setHallId(hall);
    setHallGeo({ status: "loading" });
    setAisleState({ status: "loading" });
    try {
      const booths = await fetchPublicHallBooths(hall);
      if (!booths.features.length) {
        setHallGeo({ status: "empty", message: `No booth polygons for hall ${hall}.` });
      } else {
        setHallGeo({ status: "ready", data: booths });
      }
    } catch (error) {
      setHallGeo(toLoadError(error, `Unable to load hall ${hall} geometry`));
    }
    try {
      const aisles = await fetchPublicHallAisles(hall);
      setAisleState({ status: "ready", data: aisles });
    } catch (error) {
      const status = error instanceof ApiError ? error.status : undefined;
      if (status === 404) {
        setAisleState({ status: "empty", message: `No aisle graph for hall ${hall}` });
      } else {
        setAisleState(toLoadError(error, `Unable to load aisle graph for hall ${hall}`));
      }
    }
  }, []);

  useEffect(() => {
    void loadHallMap("A");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  const goToBooth = useCallback((booth: string, label: string, hall?: string) => {
    const mapHall = resolveMapHall(hall, booth);
    setDestination({ booth_id: booth, label, hall: mapHall });
    setGoingToBooth(formatBoothHit({ booth, label, hall: mapHall }));
    setSelectedBooth(booth);
    if (mapHall && mapHall !== hallId) void loadHallMap(mapHall);
  }, [hallId, loadHallMap]);

  const hallBooths = hallGeo.status === "ready" ? hallGeo.data : null;
  const hallAisles = aisleState.status === "ready" ? aisleState.data : null;

  return (
    <div className="floor-walker-kiosk">
      <div className="workspace-toolbar">
        <div className="workspace-search">
          <BoothSearchField
            id="kiosk-booth-search"
            eventKey={EVENT_KEY}
            campaignKey={DEFAULT_CAMPAIGN_KEY}
            value={floorSearch}
            onChange={setFloorSearch}
            placeholder="Search booths ..."
            toolCaller={callPublicTool}
            onPick={(hit) => {
              if (searchPickRef.current) {
                searchPickRef.current(hit);
                return;
              }
              goToBooth(hit.booth, hit.label, hit.hall);
            }}
          />
        </div>
      </div>
      <FloorWorkspace
        eventKey={EVENT_KEY}
        campaignKey={DEFAULT_CAMPAIGN_KEY}
        actor="kiosk"
        signedInName=""
        hallId={hallId}
        hallGeo={hallGeo}
        hallBooths={hallBooths}
        hallAisles={hallAisles}
        selectedBooth={selectedBooth}
        originBooth={checkInBooth}
        checkInQuery={checkInQuery}
        onCheckInQuery={setCheckInQuery}
        onOriginBooth={setCheckInBooth}
        destination={destination}
        goingToBooth={goingToBooth}
        onGoingToBooth={setGoingToBooth}
        onDestination={setDestination}
        displayName=""
        presence={[]}
        presenceConnected={false}
        onHallChange={(hall) => void loadHallMap(hall)}
        onSelectBooth={setSelectedBooth}
        searchPickRef={searchPickRef}
        filters={filters}
        onFiltersChange={setFilters}
        filterDrawerOpen={filterOpen}
        onFilterDrawerOpenChange={setFilterOpen}
      />
    </div>
  );
}
